import { FC } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Link, Typography, Theme, Box, Grid, Divider } from '@material-ui/core'
import ApolloClient from 'apollo-client'
import { ApolloConsumer } from 'react-apollo'
import { FormattedMessage } from 'react-intl'
import clsx from 'clsx'
import CircleLoader from '@src/components/CircleLoader'
import vrStyles from '@src/styles/utils/vrStyles'
import { useCertificateContentSearchQuery } from '@src/queries/CertificateContentSearchQuery.generated'
import { QmDocumentType } from '@src/utils/analytics/enums'
import { CertificateType } from '@src/types/graphql-types'
import { useDocumentationCertificate } from './useDocumentationCertificate'
import { DocumentationLookup } from './DocumentationLookup'
import { sendCertificatesDownloadEvent } from '@src/utils/analytics'
import { NoDocumentationFound } from './NoDocumentationFound'

type DocumentationProps = {
  brand: string
  certificateType: CertificateType
  documentType: string
  productNumber: string
  title: string
  // TEMP PROP -> WILL BE REMOVED
  isCrmProduct: boolean
  isExpandedLayout?: boolean
}

export const useStyles = makeStyles((theme: Theme) => {
  const { vr1 } = vrStyles

  return {
    documentItemContainer: {
      marginBottom: theme.spacing(2),
    },
    documentLink: {
      cursor: 'pointer',
    },
    expandedLayout: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: theme.shape.borderRadius,
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(6),
      },
      [theme.breakpoints.down('md')]: {
        border: '0px',
        padding: theme.spacing(0, 0, 0),
      },
    },
    lotContentContainer: {
      margin: theme.spacing(2, 0),
    },
    lotNumber: {
      marginBottom: theme.spacing(2),
    },
    title: {
      marginBottom: theme.spacing(2),
    },
    titleContainer: {
      display: 'block',
      ...vr1,
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
        alignItems: 'center',
      },
    },
  }
})

export const Documentation: FC<DocumentationProps> = ({
  brand,
  certificateType,
  documentType,
  isCrmProduct,
  isExpandedLayout = true,
  productNumber,
  title,
}) => {
  const { data } = useCertificateContentSearchQuery({
    variables: {
      input: {
        brand,
        documentType,
        productNumber,
      },
    },
  })

  const { downloadCertificatePdf, pdfLoading, showCertificatePdf } =
    useDocumentationCertificate()

  const classes = useStyles()

  const documentationContent = data?.getCertificateContent?.content

  const isCOA = documentType === QmDocumentType.CofAnalysisDoc

  const handleDocumentClickGAevent = (fileExtension: string, lotNumber) => {
    sendCertificatesDownloadEvent({
      fileExtension,
      productNumber,
      lotNumber,
    })
  }

  const handleDocumentClick = (client: ApolloClient<object>, content) => {
    if (content?.text?.source === 'GDI') {
      handleDocumentClickGAevent('pdf', content.lotNumber)
      const certificateContent = {
        lotnumber: content?.lotNumber,
        owner: content?.text?.owner,
        term: productNumber,
        type: certificateType,
        unit: content?.text?.unit,
      }
      downloadCertificatePdf({
        certificateContent,
        client,
      })
    } else if (content.url) {
      handleDocumentClickGAevent('html', content.lotNumber)
      window.open(content.url, '_blank')
    } else {
      handleDocumentClickGAevent('html', content.lotNumber)
      const certificateContent = {
        brand,
        productNumber,
        lotNumber: content?.lotNumber,
      }
      showCertificatePdf({ certificateContent, isCOA })
    }
  }

  return (
    <ApolloConsumer>
      {(client) => (
        <Box className={clsx(isExpandedLayout && classes.expandedLayout)}>
          <Typography className={classes.title} variant="h3">
            {title}
          </Typography>
          <Grid className={classes.lotContentContainer} container>
            <Grid item xs={6}>
              <Typography variant="caption">
                <FormattedMessage
                  id="DOCUMENTATION_LOT_BATCH_NUMBER"
                  defaultMessage="Lot/Batch Number"
                />
              </Typography>
            </Grid>
          </Grid>
          {!isCrmProduct &&
            documentationContent?.slice(0, 5)?.map((content) => (
              <Grid
                className={classes.documentItemContainer}
                container
                key={content?.id}
              >
                <Grid item xs={6}>
                  <Link
                    className={classes.documentLink}
                    onClick={() => handleDocumentClick(client, content)}
                    rel="nofollow"
                  >
                    {content?.lotNumber}
                  </Link>
                </Grid>
              </Grid>
            ))}

          {!documentationContent?.length || isCrmProduct ? (
            <NoDocumentationFound
              documentType={documentType}
              productNumber={productNumber}
            />
          ) : (
            <>
              <Divider />

              <DocumentationLookup
                documentType={documentType}
                productNumber={productNumber}
              />
            </>
          )}

          {pdfLoading && <CircleLoader />}
        </Box>
      )}
    </ApolloConsumer>
  )
}

export default Documentation
